import React, { useState, useEffect } from 'react';
import { Grid, Card, Typography, TextField, Button, Dialog, DialogContent, Toolbar, CardContent, DialogActions, FormControl, FormControlLabel, Checkbox } from '@mui/material';
import Config from '../../config';
import axios from 'axios';
import EnhancedTable from '../../components/table';
import EventTable from '../../components/event_table';
import Moment from 'react-moment';
import { Form as FormBase, Field as FieldBase } from 'react-final-form';
import ExtendedField from '../../components/text_field';
import FastSelect from '../../components/fast_suggest_field';
import Switch from '../../components/switch';
import Helper from '../../helpers';
import ConfirmDialog from '../../components/confirm_dialog';

// react-final-form's Form/Field generics don't line up with this file's loose
// value shapes; cast to any at the import boundary (same pattern as
// reports.tsx/scorecards.tsx/search.tsx).
const Form: any = FormBase;
const Field: any = FieldBase;

type Webhook = {
    Id?: number;
    Topic?: string;
    Address?: string;
    Format?: string;
    FormatUpper?: string;
    SharedSecret?: string;
    Enabled?: boolean;
    [key: string]: any;
};

type ApiAuthenticationProps = {
    setTitle?: (title: string) => void;
    hasPermission?: boolean;
    isBillable?: boolean;
};

type ApiAuthenticationState = {
    primaryKey: string;
    secondaryKey: string;
    loaded: boolean;
    showWebhook: boolean;
    webhook: Webhook;
    webhookTopics: { label: string; value: string }[];
    showConfirm: boolean;
    showError: boolean;
    hasPermission?: boolean;
    refresh?: boolean;
    handleConfirm?: () => void;
    confirmContent?: string;
    disableEdit?: boolean;
    refreshWebhooks?: boolean;
    errorContent?: string;
    // Write-only in the original .js (never read back anywhere) but reset
    // here on every regenerateKey confirm; kept in the type so that setState
    // call can be preserved verbatim rather than silently dropping fields.
    keyNumber?: number | null;
    key?: string | null;
};

const eventFilter = {filters: [
      {field: "CustomerViewable", operator: 'eq', value: true},
      {filters: [
          {field: "EventTypeId", operator: 'eq', value: 68},
      ],
      logic: 'or'}
  ], logic: 'and'};

const ApiAuthentication = (props: ApiAuthenticationProps) => {
    const [state, setStateRaw] = useState<ApiAuthenticationState>({
        primaryKey: '', secondaryKey: '', loaded: false, showWebhook: false, webhook: {}, webhookTopics: [],
        showConfirm: false, showError: false
    });
    // Preserve React.Component's shallow-merge setState semantics: apply the
    // partial on top of the previous state via a functional updater.
    const setState = (partial: Partial<ApiAuthenticationState>) =>
        setStateRaw(prev => ({ ...prev, ...partial }));

    const loadData = async () => {
        var key1 = (await axios.get(Config.api + '/odata/Company/Functions.GetAPIKey?keyNumber=1')).data.value;
        var key2 = (await axios.get(Config.api + '/odata/Company/Functions.GetAPIKey?keyNumber=2')).data.value;
        setState({primaryKey: key1 || '', secondaryKey: key2 || '', hasPermission: true, loaded: true});
    };

    useEffect(() => {
        if(props.setTitle) {
            props.setTitle('API Connection');
        }
        if(props.hasPermission){
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const regenerateKey = async (keyNumber: number) => {
        let key = keyNumber === 1 ? 'primary' : 'secondary';
        setState({
            showConfirm: true,
            handleConfirm: async () => {
                await axios.get(Config.api + '/odata/Company/Functions.ResetAPIKey?keyNumber=' + keyNumber);
                await loadData();
                setStateRaw(prev => ({...prev, showConfirm: false, keyNumber: null, key: null, refresh: !prev.refresh}));
            },
            confirmContent: `Are you sure you want to regenerate the ${key} API key?`
        });
    };
    const getWebhooks = async () => {
        let data = (await axios.get(Config.api + '/api/v1/webhooks')).data;
        for(var i = 0; i < data.Body.length; i++){
            data.Body[i].FormatUpper = (data.Body[i].Format || '').toUpperCase();
        }
        return data.Body;
    };
    const saveWebhook = async (values: Webhook) => {
        setState({disableEdit: true});
        try{
            if(values.Id){
                await axios.put(Config.api + `/api/v1/webhooks/${values.Id}`, values);
            } else {
                await axios.post(Config.api + `/api/v1/webhooks`, values);
            }
            setStateRaw(prev => ({...prev, showWebhook: false, refreshWebhooks: !prev.refreshWebhooks}));
        } catch(e: any) {
            var errors = ["Unexpected error occurred."];
            if(e.response){
                errors = new Helper().getApiErrors(e.response.data);
            }
            setState({disableEdit: false, showError: true, errorContent: errors.join('\n')});
        }
    };
    const showWebhook = async (webhook: Webhook) => {
        let topics = state.webhookTopics;
        if(!topics || topics.length === 0){
            let events = (await axios.get(Config.api + '/api/v1/activityevents/eventtypes')).data.Body;
            topics = events.filter((r: any) => r.Topic).map((r: any) => ({label: `${r.Name} (${r.Topic})`, value: r.Topic}));
        }
        setState({webhook, showWebhook: true, webhookTopics: topics, disableEdit: false});
    };
    const deleteWebhook = (row: Webhook) => {
        setState({
            showConfirm: true,
            confirmContent: `Are you sure you want to delete this webhook?`,
            handleConfirm: async () => {
                try {
                    await axios.delete(Config.api + `/api/v1/webhooks/${row.Id}`);
                    setStateRaw(prev => ({...prev, showConfirm: false, refreshWebhooks: !prev.refreshWebhooks}));
                } catch(e: any) {
                    var errors = ["Unexpected error occurred."];
                    if(e.response){
                        errors = new Helper().getApiErrors(e.response.data);
                    }
                    setState({showConfirm: false, showError: true, errorContent: errors.join('\n')});
                }
            }
        });
    };
    const newWebhook = () => {
        showWebhook({
            Enabled: true,
            Format: 'json',
            SharedSecret: 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                let r = Math.random() * 16 | 0, v = c === 'x' ? r : ((r & 0x3) | 0x8);
                return v.toString(16);
            }).toUpperCase()
        });
    };

        let webhookConfig = {
            columns: [
              { id: 'Topic', label: 'Topic', width: '12em', filterable: true },
              { id: 'FormatUpper', label: 'Format', filterable: true, width: '12em' },
              { id: 'Address', label: 'Address', filterable: true, allowStringOperators: true },
              { id: 'CreatedAt', type:'date', filterable: true, sortable: true, label: 'Created', template: (value: any) => (<Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment>), width: '15em' },
              { id: 'UpdatedAt', type:'date', filterable: true, sortable: true, label: 'Updated', template: (value: any) => value ? (<Moment format='MM/DD/YYYY hh:mm A'>{value}</Moment>) : '', width: '15em' },
              { id: 'Enabled', sortable: true, label: 'Enabled', width: '12em', template: (value: any, row: any) => (<Checkbox checked={value}/>) },
              { command: 'commands', width: '16em', stopPropagation: 'true', template: (value: any, row: Webhook) => {return (<>
                  <Button style={{marginRight: '1em'}} variant='outlined' onClick={() => showWebhook(row)}>View</Button>
                  <Button variant='outlined' onClick={() => deleteWebhook(row)}>Delete</Button>
              </>);} }
          ],
          getData: getWebhooks,
          order: 'desc',
          orderBy: 'Id',
          keyField: 'Id',
          pageSize: 10,
          pageSizes: [10, 20, 50],
          refresh: state.refreshWebhooks,
          title: 'Webhooks',
          actions: <div>
            <Typography>Webhooks for new documents (orders, shipments, etc.) can only be sent to the company receiving the document. This type of webhook also requires an additional setup step on the Logicbroker side.</Typography>
            <div style={{textAlign: 'left', marginTop: '0.5em'}}><Button size='small' variant='outlined' onClick={newWebhook}>New Webhook</Button></div>
            </div>
        };
        return <Grid container spacing={2}>
            <Grid size={{ md: 12 }}>
                <Card>
                    <Toolbar className='lbtoolbar'>{`Logicbroker API Information`}</Toolbar>
                    <CardContent style={{textAlign: 'left'}}>
                        The Logicbroker API URL for this environment is <a href={Config.api} target='_blank' rel='noopener noreferrer'>{Config.api}</a>. There is detailed developer documentation available at <a href='https://dev.logicbroker.com' target='_blank' rel='noopener noreferrer'>dev.logicbroker.com</a>.
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ md: 12 }}>
                <Card>
                    <Toolbar className='lbtoolbar'>{'Subscription Keys'}</Toolbar>
                    <CardContent style={{textAlign: 'left'}}>
                    {props.hasPermission ?
                        <Grid container style={{textAlign: 'left'}}>
                            <Grid size={{ md: 12 }} style={{marginBottom: '1em'}}>
                                {state.primaryKey ?
                                <><TextField disabled label='Primary Key' value={state.primaryKey} style={{width: '25em', marginRight: '1em'}}/><span style={{lineHeight: '46px'}}><Button variant='outlined' onClick={() => regenerateKey(1)}>Regenerate</Button></span></>
                                : <Button variant='outlined' onClick={() => regenerateKey(1)}>Create Primary Key</Button> }
                            </Grid>
                            <Grid size={{ md: 12 }}>
                            {state.secondaryKey ?
                                <><TextField disabled label='Secondary Key' value={state.secondaryKey} style={{width: '25em', marginRight: '1em'}}/><span style={{lineHeight: '46px'}}><Button variant='outlined' onClick={() => regenerateKey(2)}>Regenerate</Button></span></>
                                : <Button variant='outlined' onClick={() => regenerateKey(2)}>Create Secondary Key</Button> }
                            </Grid>
                        </Grid>
                        : 'You do not have permission to manage API keys.'}
                    </CardContent>
                </Card>
            </Grid>
            {props.isBillable && <Grid size={12}>
                <EnhancedTable config={webhookConfig}/>
            </Grid>}
            {!props.isBillable && <Grid size={12}>
            <Card>
                <Toolbar className='lbtoolbar'>{'Webhooks'}</Toolbar>
                <CardContent style={{textAlign: 'left'}}>You must have a paid subscription to manage webhooks.</CardContent>
            </Card>
            </Grid>}
            <Grid size={12}>
                <EventTable title="Audit Log" filter={eventFilter} refresh={state.refresh}/>
            </Grid>
            <Dialog fullWidth maxWidth='sm' open={state.showWebhook} onClose={() => setState({showWebhook: false})}>
            <Form onSubmit={saveWebhook}
            initialValues={state.webhook}
            render={({ handleSubmit, pristine, invalid, values }: any) => (
              <form onSubmit={handleSubmit} style={{display: 'flex', flexDirection: 'column'}}>
              <Toolbar className='lbtoolbar'>{state.webhook.Id ? 'New Webhook' : 'Edit Webhook'}</Toolbar>
            <DialogContent>
                <div style={{display: 'flex', flexDirection: 'column', flexShrink: '0'}}>
                    <Field label='Topic' options={state.webhookTopics} component={FastSelect} name={"Topic"} validate={(v: any) => !v ? 'Topic is required.' : undefined}/>
                    <Field label='Address' component={ExtendedField} name='Address' validate={(v: any) => !v ? 'Address is required.' : undefined}/>
                    <Field label='Format' component={FastSelect} name='Format' options={[{label: 'JSON', value: 'json'}, {label: 'XML', value: 'xml'}]} validate={(v: any) => !v ? 'Format is required.' : undefined}/>
                    <Field label='Shared Secret' component={ExtendedField} name='SharedSecret' validate={(v: any) => !v && values.Id ? 'Shared secret is required.' : undefined}/>
                    <FormControl>
                        <FormControlLabel label='Enabled' control={<Field type="checkbox" component={Switch} name="Enabled"/>}/>
                    </FormControl>
                </div>
            </DialogContent>
            <DialogActions>
                            <Button type='submit' variant='text' disabled={pristine || invalid || state.disableEdit} color="primary">
                                Save
                            </Button>
            </DialogActions>
            </form>
        )}/>
            </Dialog>
            <Dialog maxWidth="sm" open={state.showError} onClose={() => setState({showError: false})}>
                <Toolbar className='lbtoolbar'>Error</Toolbar>
                <DialogContent>
                  <Typography>{state.errorContent}</Typography>
                </DialogContent>
              </Dialog>
            <ConfirmDialog open={state.showConfirm} onClose={() => setState({showConfirm: false})} message={state.confirmContent} onConfirm={state.handleConfirm}/>
        </Grid>;
};

export default ApiAuthentication;
